<template>
  <div class="reset-password-view">
    <div class="logo-wrapper">
      <Logo :showText="true" :isAuthLogo="true" />
    </div>
    
    <h1 class="auth-title">Reset Password</h1>
    
    <div class="auth-card">
      <div class="auth-form">
        <div v-if="!passwordReset && !tokenError">
          <p class="form-description">
            Please enter your new password below.
          </p>
          
          <form @submit.prevent="resetPassword">
            <div class="form-group">
              <input 
                type="password" 
                class="form-control" 
                placeholder="New Password" 
                v-model="password" 
                required
                minlength="8"
              >
              <div class="password-requirements">
                <small>Password must be at least 8 characters long</small>
              </div>
            </div>
            
            <div class="form-group">
              <input 
                type="password" 
                class="form-control" 
                placeholder="Confirm New Password" 
                v-model="confirmPassword" 
                required
              >
            </div>
            
            <button type="submit" class="btn btn-primary btn-block" :disabled="loading || !canSubmit">
              {{ loading ? 'Resetting...' : 'Reset Password' }}
            </button>
            
            <div class="error-message" v-if="error">{{ error }}</div>
          </form>
        </div>
        
        <div v-else-if="tokenError" class="error-state">
          <div class="error-icon">⚠</div>
          <h3>Invalid or Expired Link</h3>
          <p>This password reset link is invalid or has expired.</p>
          <p class="info-text">
            Password reset links expire after 5 minutes for security reasons.
            Please request a new password reset link.
          </p>
          
          <div class="action-buttons">
            <router-link to="/forgot-password" class="btn btn-primary">
              Request New Link
            </router-link>
            <router-link to="/login" class="btn btn-secondary">
              Back to Login
            </router-link>
          </div>
        </div>
        
        <div v-else class="success-state">
          <div class="success-icon">✓</div>
          <h3>Password Reset Successful</h3>
          <p>Your password has been reset successfully.</p>
          <p class="info-text">
            You can now log in with your new password.
          </p>
          
          <div class="action-buttons">
            <router-link to="/login" class="btn btn-primary">
              Go to Login
            </router-link>
          </div>
        </div>
      </div>
    </div>
    
    <footer class="auth-footer">
      <div>© {{ currentYear }} Top Line Marketing. All Rights Reserved.</div>
    </footer>
  </div>
</template>

<script>
import Logo from '@/components/Logo.vue'
import apiService from '@/services/apiService'

export default {
  name: 'ResetPasswordView',
  components: {
    Logo
  },
  data() {
    return {
      password: '',
      confirmPassword: '',
      loading: false,
      error: '',
      passwordReset: false,
      tokenError: false,
      token: ''
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
    canSubmit() {
      return this.password.length >= 8 && 
             this.confirmPassword && 
             this.password === this.confirmPassword;
    }
  },
  watch: {
    password() {
      this.validatePasswords();
    },
    confirmPassword() {
      this.validatePasswords();
    }
  },
  mounted() {
    // Get token from URL query parameters
    this.token = this.$route.query.token;
    
    if (!this.token) {
      this.tokenError = true;
    }
  },
  methods: {
    validatePasswords() {
      this.error = '';
      
      if (this.password && this.password.length < 8) {
        this.error = 'Password must be at least 8 characters long.';
        return;
      }
      
      if (this.confirmPassword && this.password !== this.confirmPassword) {
        this.error = 'Passwords do not match.';
        return;
      }
    },

    async resetPassword() {
      if (!this.canSubmit) {
        return;
      }

      this.loading = true;
      this.error = '';

      try {
        await apiService.post('/business/reset-password/', {
          token: this.token,
          password: this.password
        });
        
        this.passwordReset = true;
        
      } catch (error) {
        console.error('Password reset failed:', error);
        
        if (error.response && error.response.data) {
          if (error.response.data.errors) {
            // Handle validation errors
            const errors = error.response.data.errors;
            if (errors.token) {
              this.tokenError = true;
            } else if (errors.password) {
              this.error = errors.password[0];
            } else {
              this.error = 'Please check your password and try again.';
            }
          } else if (error.response.data.detail) {
            const detail = error.response.data.detail;
            if (detail.includes('expired') || detail.includes('invalid') || detail.includes('used')) {
              this.tokenError = true;
            } else {
              this.error = detail;
            }
          } else {
            this.error = 'An error occurred. Please try again.';
          }
        } else {
          this.error = 'Network error. Please check your connection and try again.';
        }
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.reset-password-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  padding: 20px;
  min-height: 100vh;
}

.logo-wrapper {
  margin-bottom: 20px;
}

.auth-title {
  font-size: 28px;
  font-weight: bold;
  margin: 30px 0;
  text-align: center;
  line-height: 1.3;
}

.auth-card {
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 450px;
  overflow: hidden;
}

.auth-form {
  padding: 30px;
  color: #333;
}

.form-description {
  text-align: center;
  color: #666;
  margin-bottom: 25px;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 20px;
}

.form-control {
  display: block;
  width: 100%;
  padding: 12px 15px;
  font-size: 16px;
  line-height: 1.5;
  color: #495057;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  border-color: #4A6282;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(74, 98, 130, 0.25);
}

.password-requirements {
  margin-top: 5px;
}

.password-requirements small {
  color: #6c757d;
  font-size: 12px;
}

.btn {
  display: inline-block;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  user-select: none;
  border: 1px solid transparent;
  padding: 12px 15px;
  font-size: 16px;
  line-height: 1.5;
  border-radius: 4px;
  transition: all 0.15s ease-in-out;
  cursor: pointer;
  text-decoration: none;
  margin: 5px;
}

.btn-block {
  width: 100%;
  margin: 5px 0;
}

.btn-primary {
  color: #fff;
  background-color: #4A6282;
  border-color: #4A6282;
}

.btn-primary:hover {
  background-color: #3d5270;
  border-color: #395069;
}

.btn-secondary {
  color: #6c757d;
  background-color: transparent;
  border-color: #6c757d;
}

.btn-secondary:hover {
  color: #fff;
  background-color: #6c757d;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.error-message {
  color: #e94057;
  margin-top: 10px;
  font-size: 14px;
  text-align: center;
}

.success-state,
.error-state {
  text-align: center;
}

.success-icon {
  font-size: 48px;
  color: #28a745;
  margin-bottom: 20px;
}

.error-icon {
  font-size: 48px;
  color: #dc3545;
  margin-bottom: 20px;
}

.success-state h3,
.error-state h3 {
  color: #333;
  margin-bottom: 15px;
}

.success-state p,
.error-state p {
  color: #666;
  margin-bottom: 10px;
}

.info-text {
  font-size: 14px;
  line-height: 1.4;
  margin: 20px 0;
}

.action-buttons {
  margin-top: 30px;
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

.auth-footer {
  margin-top: 30px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
}

@media (max-width: 480px) {
  .action-buttons {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
    margin: 5px 0;
  }
}
</style>
